import { Data, Match, Option } from 'effect';
import * as Atom from 'effect/unstable/reactivity/Atom';

import type { ListingScope, ListingScopeKey } from '../../capability/productListing/listingScope';
import {
  applyChange,
  type ListingChange,
  type ListingState,
} from '../../capability/productListing/listingSelection';
import { decodeListingUrl, encodeListingUrl, type ListingBase } from '../codec';
import type { ListingUrlSyntax } from '../url/ListingUrlSyntax';
import { fromLocation, href, type ListingUrl, type QueryPair } from '../url/listingUrl';
import type { ListingVocabulary } from '../vocabulary/ListingVocabulary';
import type { ListingHistory } from './listingHistory';

type Transition = Data.TaggedEnum<{
  Change: { readonly change: ListingChange };
  Append: {};
  Pop: { readonly url: ListingUrl };
}>;
const Transition = Data.taggedEnum<Transition>();

/** Owns Listing state transitions, page run origins, and browser history effects. */
export const makeListingNavigation = (deps: {
  readonly state: (scopeKey: ListingScopeKey) => Atom.Writable<ListingState, ListingState>;
  readonly base: (scopeKey: ListingScopeKey) => Atom.Writable<ListingBase, ListingBase>;
  readonly vocabulary: (scopeKey: ListingScopeKey) => Atom.Atom<ListingVocabulary>;
  readonly history: ListingHistory;
  readonly syntax: ListingUrlSyntax;
  readonly splitQuery: (
    scope: ListingScope,
    query: ReadonlyArray<QueryPair>
  ) => { readonly listing: ReadonlyArray<QueryPair> };
}) => {
  const prerequisites = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.make((get) => ({
      state: get(deps.state(scopeKey)),
      base: get(deps.base(scopeKey)),
      vocabulary: get(deps.vocabulary(scopeKey)),
    }))
  );
  const state = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.make((get) => get(prerequisites(scopeKey)).state)
  );
  const runStart = Atom.family((_scopeKey: ListingScopeKey) => Atom.make(Option.none<number>()));
  const firstPage = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.make((get) => Option.getOrElse(get(runStart(scopeKey)), () => get(state(scopeKey)).page))
  );
  const transition = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.writable(
      (get) => get(state(scopeKey)),
      (context, command: Transition) => {
        const current = context.get(deps.state(scopeKey));
        const vocabulary = context.get(deps.vocabulary(scopeKey));
        const target = Match.value(command).pipe(
          Match.tag('Change', ({ change }) => ({
            state: applyChange(current, change),
            firstPage: Option.none<number>(),
            base: context.get(deps.base(scopeKey)),
            push: true,
          })),
          Match.tag('Append', () => ({
            state: applyChange(current, { _tag: 'SetPage', page: current.page + 1 }),
            firstPage: Option.orElse(context.get(runStart(scopeKey)), () =>
              Option.some(current.page)
            ),
            base: context.get(deps.base(scopeKey)),
            push: true,
          })),
          Match.tag('Pop', ({ url }) => ({
            ...decodeListingUrl(
              { ...url, query: deps.splitQuery(current.scope, url.query).listing },
              current.scope,
              deps.syntax,
              vocabulary
            ),
            firstPage: Option.none<number>(),
            push: false,
          })),
          Match.exhaustive
        );
        Atom.batch(() => {
          context.set(runStart(scopeKey), target.firstPage);
          context.set(deps.base(scopeKey), target.base);
          context.set(deps.state(scopeKey), target.state);
        });
        Match.value(target.push).pipe(
          Match.when(true, () =>
            deps.history.push(
              href(encodeListingUrl(target.base, target.state, deps.syntax, vocabulary))
            )
          ),
          Match.when(false, () => undefined),
          Match.exhaustive
        );
      }
    )
  );
  const change = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.writable(
      (get) => get(state(scopeKey)),
      (context, requestedChange: ListingChange) =>
        context.set(transition(scopeKey), Transition.Change({ change: requestedChange }))
    )
  );
  const append = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.writable(
      (get) => get(state(scopeKey)),
      (context, _command: void) => context.set(transition(scopeKey), Transition.Append())
    )
  );
  const history = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.make((get) => {
      get(prerequisites(scopeKey));
      const unsubscribe = deps.history.subscribe(() =>
        get.set(
          transition(scopeKey),
          Transition.Pop({ url: fromLocation(deps.history.location()) })
        )
      );
      get.addFinalizer(unsubscribe);
      return undefined;
    })
  );
  return { firstPage, state, change, append, history };
};
